import { Add, Edit } from "@mui/icons-material"
import { Box, Button, Stack, Typography } from "@mui/material"
import React, {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { IPost } from "../../../../interface/interface"
import EditUrlButton from "./editUrlButton"
import UrlButton from "./urlButton"

export interface IUrlButtonData {
  text: string
  url: string
  icon_custom_emoji_id?: string
  style?: "danger" | "success" | "primary"
}

export type UrlButtonsMarkup = { inline_keyboard: IUrlButtonData[][] }
export type UrlButtonFieldErrors = Partial<Record<"text" | "url", string>>
export type UrlButtonsCollectResult =
  | { ok: true; markup: UrlButtonsMarkup | null }
  | { ok: false }

export interface UrlButtonsHandle {
  validateAndCollect(): UrlButtonsCollectResult
}

interface IUrlButtonProps {
  data?: UrlButtonsMarkup | IPost["reply_markup"]
  onChange: (data: IPost["reply_markup"]) => void
  validate?: (data: IUrlButtonData) => string | null
  validateRow?: (row: IUrlButtonData[]) => string | null
  hideIconField?: boolean
  validateFields?: (data: IUrlButtonData) => UrlButtonFieldErrors
  /** Проверяет структуру/количество, не поля соседних кнопок. */
  validateLayout?: (markup: UrlButtonsMarkup | null) => string | null
  /** Явная замена данных сессии; обычное эхо data не сбрасывает черновики. */
  resetKey?: string | number
}

type Field = keyof UrlButtonFieldErrors
interface ButtonState {
  id: number
  draft: IUrlButtonData
  committed?: IUrlButtonData
  edit: boolean
  touched: Partial<Record<Field, boolean>>
  attempted: boolean
}
interface RowState {
  id: number
  buttons: ButtonState[]
}
interface EditorState {
  rows: RowState[]
  nextId: number
}
type FocusTarget = { id: number; field: Field } | "section"

const copyButton = (
  data: IUrlButtonData,
  hideIconField = false,
): IUrlButtonData => {
  const { icon_custom_emoji_id, ...rest } = data
  return hideIconField ? { ...rest } : { ...data }
}

const createState = (
  data: IUrlButtonProps["data"],
  nextId = 0,
): EditorState => {
  const rows = (data?.inline_keyboard ?? []).map((row) => ({
    id: nextId++,
    buttons: row.map((button) => ({
      id: nextId++,
      committed: copyButton(button),
      draft: copyButton(button),
      edit: false,
      touched: {},
      attempted: false,
    })),
  }))
  return { rows, nextId }
}

const serialize = (
  state: EditorState,
  committed: boolean,
  hideIconField?: boolean,
): UrlButtonsMarkup | null => {
  const rows = state.rows
    .map((row) =>
      row.buttons.flatMap((button) => {
        const data = committed ? button.committed : button.draft
        return data ? [copyButton(data, hideIconField)] : []
      }),
    )
    .filter((row) => row.length > 0)
  return rows.length ? { inline_keyboard: rows } : null
}

const updateButton = (
  state: EditorState,
  id: number,
  update: (button: ButtonState) => ButtonState,
): EditorState => ({
  ...state,
  rows: state.rows.map((row) => ({
    ...row,
    buttons: row.buttons.map((button) =>
      button.id === id ? update(button) : button,
    ),
  })),
})

const UrlButtons = forwardRef<UrlButtonsHandle, IUrlButtonProps>(
  function UrlButtons(props, ref) {
    const [state, setState] = useState(() => createState(props.data))
    const snapshot = useRef(state)
    const resetKey = useRef(props.resetKey)
    const sectionRef = useRef<HTMLDivElement>(null)
    const fieldRefs = useRef(
      new Map<number, Partial<Record<Field, HTMLInputElement>>>(),
    )
    const focusTarget = useRef<FocusTarget | null>(null)

    // Обработчики читают последний снимок даже до рендера следующего React batch.
    // Вычисление перехода чистое; callbacks выполняются отдельно, ровно один раз.
    const transition = (update: (current: EditorState) => EditorState) => {
      const next = update(snapshot.current)
      snapshot.current = next
      setState(next)
      return next
    }

    useLayoutEffect(() => {
      if (resetKey.current !== props.resetKey) {
        resetKey.current = props.resetKey
        focusTarget.current = null
        const next = createState(props.data, snapshot.current.nextId)
        snapshot.current = next
        setState(next)
      }
    }, [props.resetKey, props.data])

    useLayoutEffect(() => {
      const target = focusTarget.current
      if (!target) return
      focusTarget.current = null
      if (target === "section") sectionRef.current?.focus()
      else fieldRefs.current.get(target.id)?.[target.field]?.focus()
    }, [state])

    const fieldErrors = (button: ButtonState) =>
      props.validateFields?.(copyButton(button.draft, props.hideIconField)) ??
      {}
    const legacyError = (button: ButtonState) =>
      props.validate?.(copyButton(button.draft, props.hideIconField)) ?? null
    const firstFieldError = (button: ButtonState): Field | undefined => {
      const errors = fieldErrors(button)
      return errors.text ? "text" : errors.url ? "url" : undefined
    }
    const layoutError = (current: EditorState, rowId?: number) =>
      props.validateLayout?.(serialize(current, false, props.hideIconField)) ??
      current.rows
        .filter((row) => rowId === undefined || row.id === rowId)
        .map((row) =>
          props.validateRow?.(
            row.buttons.map((button) =>
              copyButton(button.draft, props.hideIconField),
            ),
          ),
        )
        .find((error) => !!error) ??
      null

    const validateAndCollect = (): UrlButtonsCollectResult => {
      const current = snapshot.current
      let firstInvalid: FocusTarget | null = null
      const invalidIds = new Set<number>()
      for (const row of current.rows) {
        for (const button of row.buttons) {
          const field = firstFieldError(button)
          if (field || legacyError(button)) {
            invalidIds.add(button.id)
            firstInvalid ??= { id: button.id, field: field ?? "text" }
          }
        }
      }
      const invalidLayout = layoutError(current)
      if (firstInvalid || invalidLayout) {
        focusTarget.current = firstInvalid ?? "section"
        transition((model) => ({
          ...model,
          rows: model.rows.map((row) => ({
            ...row,
            buttons: row.buttons.map((button) => ({
              ...button,
              attempted: true,
              edit: button.edit || invalidIds.has(button.id),
            })),
          })),
        }))
        return { ok: false }
      }
      return {
        ok: true,
        markup: serialize(current, false, props.hideIconField),
      }
    }

    useImperativeHandle(ref, () => ({ validateAndCollect }))

    const addButton = (rowId?: number) => {
      transition((current) => {
        let nextId = current.nextId
        const row = current.rows.find((item) => item.id === rowId)
        const newRowId = row ? row.id : nextId++
        const button: ButtonState = {
          id: nextId++,
          draft: { text: "", url: "https://" },
          edit: true,
          touched: {},
          attempted: false,
        }
        const rows = row
          ? current.rows.map((item) =>
              item.id === row.id
                ? { ...item, buttons: [...item.buttons, button] }
                : item,
            )
          : [...current.rows, { id: newRowId, buttons: [button] }]
        return { rows, nextId }
      })
    }

    const removeButton = (id: number) => {
      const next = transition((current) => ({
        ...current,
        rows: current.rows
          .map((row) => ({
            ...row,
            buttons: row.buttons.filter((button) => button.id !== id),
          }))
          .filter((row) => row.buttons.length > 0),
      }))
      props.onChange(
        serialize(next, true, props.hideIconField) ?? { inline_keyboard: [] },
      )
    }

    const saveButton = (id: number) => {
      const current = snapshot.current
      const row = current.rows.find((row) =>
        row.buttons.some((button) => button.id === id),
      )
      const button = row?.buttons.find((button) => button.id === id)
      if (!row || !button) return
      const field = firstFieldError(button)
      if (field || legacyError(button) || layoutError(current, row.id)) {
        focusTarget.current = field ? { id, field } : "section"
        transition((model) =>
          updateButton(model, id, (button) => ({ ...button, attempted: true })),
        )
        return
      }
      const next = transition((model) =>
        updateButton(model, id, (button) => {
          const data = copyButton(button.draft, props.hideIconField)
          return {
            ...button,
            draft: data,
            committed: copyButton(data),
            edit: false,
            touched: {},
            attempted: false,
          }
        }),
      )
      props.onChange(
        serialize(next, true, props.hideIconField) ?? { inline_keyboard: [] },
      )
    }

    const shownLayoutError = layoutError(state)

    return (
      <>
        <Typography variant="body1">Кнопки:</Typography>
        <Stack
          direction="column"
          spacing={1}
          role="group"
          aria-label="Кнопки"
          tabIndex={-1}
          ref={sectionRef}
        >
          {state.rows.map((row) => (
            <Stack direction="row" spacing={1} key={row.id}>
              {row.buttons.map((button) => {
                const errors = fieldErrors(button)
                const shownErrors = {
                  text:
                    button.attempted || button.touched.text
                      ? errors.text
                      : undefined,
                  url:
                    button.attempted || button.touched.url
                      ? errors.url
                      : undefined,
                }
                return (
                  <Box sx={{ width: "100%" }} key={button.id}>
                    {button.edit ? (
                      <EditUrlButton
                        data={button.draft}
                        errors={shownErrors}
                        error={button.attempted ? legacyError(button) : null}
                        hideIconField={props.hideIconField}
                        onChange={(patch, field) =>
                          transition((model) =>
                            updateButton(model, button.id, (current) => ({
                              ...current,
                              draft: { ...current.draft, ...patch },
                              touched: field
                                ? { ...current.touched, [field]: true }
                                : current.touched,
                            })),
                          )
                        }
                        onInputRef={(field, element) => {
                          const fields = fieldRefs.current.get(button.id) ?? {}
                          if (element) {
                            fields[field] = element
                            fieldRefs.current.set(button.id, fields)
                          } else {
                            delete fields[field]
                            if (!Object.keys(fields).length)
                              fieldRefs.current.delete(button.id)
                          }
                        }}
                        onRemove={() => removeButton(button.id)}
                        onSave={() => saveButton(button.id)}
                      />
                    ) : (
                      <Stack direction="row" sx={{ width: "100%" }}>
                        <UrlButton data={button.committed ?? button.draft} />
                        <Button
                          aria-label={`Редактировать кнопку ${button.draft.text}`}
                          onClick={() =>
                            transition((model) =>
                              updateButton(model, button.id, (current) => ({
                                ...current,
                                draft: copyButton(
                                  current.committed ?? current.draft,
                                ),
                                edit: true,
                                touched: {},
                                attempted: false,
                              })),
                            )
                          }
                        >
                          <Edit />
                        </Button>
                      </Stack>
                    )}
                  </Box>
                )
              })}
              <Button
                aria-label="Добавить кнопку в строку"
                onClick={() => addButton(row.id)}
              >
                <Add />
              </Button>
            </Stack>
          ))}
          <Button
            aria-label="Добавить строку кнопок"
            onClick={() => addButton()}
          >
            <Add />
          </Button>
          {shownLayoutError && (
            <Typography color="error" variant="caption" role="alert">
              {shownLayoutError}
            </Typography>
          )}
        </Stack>
      </>
    )
  },
)

export default UrlButtons
